import {useId} from 'react';
import clsx from 'clsx';
import styles from './TrigWidget.module.css';

const points = (fn, x0, x1, n) => Array.from({length: n + 1}, (_, i) => {
    const x = x0 + ((x1 - x0) * i) / n;
    return `${x.toFixed(1)} ${fn(x).toFixed(1)}`;
});

/** y = 75 - 30 sin θ over x=80..290 (θ 0..2π) - the amplitude matches the unit circle's radius. */
const SINE_PATH = `M${points((x) => 75 - 30 * Math.sin(((x - 80) / 210) * 2 * Math.PI), 80, 290, 70).join(' L')}`;

const AXIS_TICKS = [
    {x: 132.5, label: '90°'},
    {x: 185, label: '180°'},
    {x: 237.5, label: '270°'},
    {x: 290, label: '360°'},
];

/** Unit circle drives the sine curve: the point's height on the circle traces y = sin θ. */
export default function TrigWidget({playing}) {
    const clipId = useId();

    return (
        <>
            <div className={styles.chips}>
                <span className={styles.chipActive}><span className={styles.chipDot}/>y = sin θ</span>
                <span className={styles.chipInactive}>cos θ</span>
                <span className={styles.chipInactive}>tan θ</span>
            </div>
            <div className={styles.plot}>
                <svg viewBox="0 0 300 150" className={styles.svg}>
                    <defs>
                        <clipPath id={clipId}>
                            <rect x="80" y="0" width="210" height="150" className={clsx(styles.revealRect, playing && styles.animated)}/>
                        </clipPath>
                    </defs>
                    <circle cx="36" cy="75" r="30" fill="var(--color-primary-50)" stroke="var(--color-primary-400)" strokeWidth="2"/>
                    <line x1="6" y1="75" x2="66" y2="75" stroke="var(--color-primary-200)" strokeWidth="1"/>
                    <line x1="36" y1="45" x2="36" y2="105" stroke="var(--color-primary-200)" strokeWidth="1"/>
                    <line x1="80" y1="75" x2="294" y2="75" stroke="var(--color-neutral-300)" strokeWidth="1.5"/>
                    <line x1="80" y1="38" x2="80" y2="112" stroke="var(--color-neutral-300)" strokeWidth="1.5"/>
                    {AXIS_TICKS.map((tick) => (
                        <line key={tick.x} x1={tick.x} y1="71" x2={tick.x} y2="79" stroke="var(--color-neutral-400)" strokeWidth="1.5"/>
                    ))}
                    {AXIS_TICKS.map((tick) => (
                        <text key={tick.label} x={tick.x} y="132" fill="var(--color-neutral-500)" textAnchor="middle" className={styles.axisLabel}>
                            {tick.label}
                        </text>
                    ))}
                    <text x="72" y="49" fill="var(--color-neutral-500)" textAnchor="end" className={styles.axisLabel}>1</text>
                    <text x="72" y="109" fill="var(--color-neutral-500)" textAnchor="end" className={styles.axisLabel}>−1</text>
                    <path d={SINE_PATH} fill="none" stroke="#FDE8D6" strokeWidth="3.5" strokeLinecap="round"/>
                    <path d={SINE_PATH} fill="none" stroke="var(--home-color-orange)" strokeWidth="3.5" strokeLinecap="round" clipPath={`url(#${clipId})`}/>
                    <g className={clsx(styles.radiusGroup, playing && styles.animated)}>
                        <line x1="36" y1="75" x2="66" y2="75" stroke="var(--color-neutral-900)" strokeWidth="2.5"/>
                        <circle cx="66" cy="75" r="5.5" fill="var(--home-color-orange)" stroke="var(--color-neutral-900)" strokeWidth="1.5"/>
                    </g>
                </svg>
                <div className={clsx(styles.tracerTrack, playing && styles.animated)}>
                    <div className={styles.guide}/>
                    <div className={clsx(styles.tracerDot, playing && styles.animated)}/>
                </div>
            </div>
            <span className={styles.caption}>Height of the point on the circle → <em>y</em> = sin θ</span>
        </>
    );
}
